import React, { Suspense, useState, useEffect, useMemo, useRef } from 'react';
import { Layout, Menu, Tabs, Dropdown, Avatar, Space, Modal, message, Button, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { 
  AppstoreOutlined, 
  UserOutlined, 
  TeamOutlined, 
  ShoppingCartOutlined,
  FileTextOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  SettingOutlined,
  UsergroupAddOutlined,
  LogoutOutlined,
  ProfileOutlined,
  ShopOutlined,
  DollarOutlined,
  DownOutlined,
  ReloadOutlined,
  CloseOutlined,
  SafetyOutlined,
  DashboardOutlined,
  DeleteOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { PermissionRoute, PublicRoute, PortalRoute } from './components/RouteGuard';
import { isAuthenticated, getCurrentUserName, getCurrentUserRole, clearLoginInfo, filterMenuByRole, getRoleName } from './utils/auth';
import { systemParamAPI, staffAPI, authAPI } from './api';
import { useTheme } from './contexts/ThemeContext';

import './App.css';
import Dashboard from './pages/Dashboard';

const { Header, Content, Sider } = Layout;

// 全局默认字体栈：微软雅黑优先，逐级回退系统字体
const APP_FONT_FAMILY = "'Microsoft YaHei', '微软雅黑', -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Segoe UI', 'Helvetica Neue', sans-serif";

// 导入页面组件
const ProductList = React.lazy(() => import('./pages/ProductList'));
const SupplierList = React.lazy(() => import('./pages/SupplierList'));
const CustomerList = React.lazy(() => import('./pages/CustomerList'));
const PurchaseOrderList = React.lazy(() => import('./pages/PurchaseOrderList'));
const InventoryList = React.lazy(() => import('./pages/InventoryList'));
const Reports = React.lazy(() => import('./pages/Reports'));
const StaffList = React.lazy(() => import('./pages/StaffList'));
const RoleList = React.lazy(() => import('./pages/RoleList'));
const DepartmentList = React.lazy(() => import('./pages/DepartmentList'));
const PositionList = React.lazy(() => import('./pages/PositionList'));
const SystemParams = React.lazy(() => import('./pages/SystemParamSettings'));
const InventoryAlert = React.lazy(() => import('./pages/InventoryAlert'));
const PurchaseOrderCreate = React.lazy(() => import('./pages/PurchaseOrderCreate'));
const PurchaseOrderDetail = React.lazy(() => import('./pages/PurchaseOrderDetail'));
const ProductCreate = React.lazy(() => import('./pages/ProductCreate'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const ProductEdit = React.lazy(() => import('./pages/ProductEdit'));
const CustomerDetail = React.lazy(() => import('./pages/CustomerDetail'));
const CustomerCreate = React.lazy(() => import('./pages/CustomerCreate'));
const CustomerEdit = React.lazy(() => import('./pages/CustomerEdit'));
const SupplierDetail = React.lazy(() => import('./pages/SupplierDetail'));
const SupplierEdit = React.lazy(() => import('./pages/SupplierEdit'));
const SupplierCreate = React.lazy(() => import('./pages/SupplierCreate'));
const StaffDetail = React.lazy(() => import('./pages/StaffDetail'));
const StaffCreate = React.lazy(() => import('./pages/StaffCreate'));
const StaffEdit = React.lazy(() => import('./pages/StaffEdit'));
const PurchaseOrderEdit = React.lazy(() => import('./pages/PurchaseOrderEdit'));
const PurchaseInvoiceList = React.lazy(() => import('./pages/PurchaseInvoiceList'));
const SalesOrderList = React.lazy(() => import('./pages/SalesOrderList'));
const SalesOrderCreate = React.lazy(() => import('./pages/SalesOrderCreate'));
const SalesOrderDetail = React.lazy(() => import('./pages/SalesOrderDetail'));
const SalesOrderEdit = React.lazy(() => import('./pages/SalesOrderEdit'));
const PurchaseReturnList = React.lazy(() => import('./pages/PurchaseReturnList'));
const PurchaseReturnCreate = React.lazy(() => import('./pages/PurchaseReturnCreate'));
const PurchaseReturnDetail = React.lazy(() => import('./pages/PurchaseReturnDetail'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const Login = React.lazy(() => import('./pages/Login'));
const ProductCategoryList = React.lazy(() => import('./pages/ProductCategoryList'));
const CashFlowList = React.lazy(() => import('./pages/CashFlowList'));
// 维修工单管理（后台）
const RepairOrderList = React.lazy(() => import('./pages/repair/RepairOrderList'));
const RepairOrderDetail = React.lazy(() => import('./pages/repair/RepairOrderDetail'));
const RepairCustomerList = React.lazy(() => import('./pages/repair/RepairCustomerList'));
const RepairSettlementList = React.lazy(() => import('./pages/repair/RepairSettlementList'));
const RepairStats = React.lazy(() => import('./pages/repair/RepairStats'));
// 客户自助维修门户
const PortalLogin = React.lazy(() => import('./pages/portal/PortalLogin'));
const PortalHome = React.lazy(() => import('./pages/portal/PortalHome'));
const PortalApply = React.lazy(() => import('./pages/portal/PortalApply'));
const PortalOrderList = React.lazy(() => import('./pages/portal/PortalOrderList'));
const PortalOrderDetail = React.lazy(() => import('./pages/portal/PortalOrderDetail'));
const PortalProfile = React.lazy(() => import('./pages/portal/PortalProfile'));
const WarehouseList = React.lazy(() => import('./pages/WarehouseList'));
const SystemLogs = React.lazy(() => import('./pages/SystemLogs'));
const OrderRecycleBin = React.lazy(() => import('./pages/OrderRecycleBin'));
const BrandList = React.lazy(() => import('./pages/BrandList'));

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([
    {
      key: '/',
      label: '仪表盘',
      closable: false,
    },
  ]);
  const [activeKey, setActiveKey] = useState('/');
  // 刷新计数：变化时通过 key 强制重建 Routes，实现"刷新当前页"
  const [refreshTick, setRefreshTick] = useState(0);
  // 使用 ref 保持最新的 tabs 引用，避免事件处理器闭包中读到旧值
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const [systemName, setSystemName] = useState('进销存系统');
  const [userName, setUserName] = useState('管理员');
  const [userRole, setUserRole] = useState(null);
  const [userAvatar, setUserAvatar] = useState(null); // 添加头像状态
  const [companyLogo, setCompanyLogo] = useState(null); // 企业LOGO
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);

  // 设置 dayjs 全局中文
  useEffect(() => {
    dayjs.locale('zh-cn');
  }, []);

  // 初始化用户信息
  useEffect(() => {
    if (isAuthenticated()) {
      const storedUserName = getCurrentUserName();
      const storedUserRole = getCurrentUserRole();
      const storedUserId = localStorage.getItem('userId');
      
      if (storedUserName) {
        setUserName(storedUserName);
      }
      // 修复：即使 roleId 为 0 也应该设置，所以检查 null/undefined
      if (storedUserRole !== null && storedUserRole !== undefined) {
        setUserRole(storedUserRole);
      }
      
      // 加载用户头像
      if (storedUserId) {
        fetchUserAvatar(storedUserId);
      }
      
      // 清理可能存在的"未知页面"标签
      setTabs(prevTabs => prevTabs.filter(tab => tab.label !== '未知页面'));
    }

    // 监听用户信息更新事件
    const handleUserInfoUpdate = () => {
      console.log('userInfoUpdated 事件触发!');
      const updatedUserName = getCurrentUserName();
      const updatedUserRole = getCurrentUserRole();
      const updatedUserId = localStorage.getItem('userId');
      console.log('更新的userId:', updatedUserId);
      
      if (updatedUserName) {
        setUserName(updatedUserName);
      }
      if (updatedUserRole !== null && updatedUserRole !== undefined) {
        setUserRole(updatedUserRole);
      }
      // 重新加载头像
      if (updatedUserId) {
        console.log('准备重新加载头像...');
        fetchUserAvatar(updatedUserId);
      } else {
        console.log('userId 为空，无法加载头像');
      }
    };

    window.addEventListener('userInfoUpdated', handleUserInfoUpdate);
    return () => {
      window.removeEventListener('userInfoUpdated', handleUserInfoUpdate);
    };
  }, []);

  // 获取用户头像
  const fetchUserAvatar = async (userId) => {
    try {
      console.log('正在获取用户头像, userId:', userId);
      const response = await staffAPI.getStaff(userId);
      console.log('获取用户信息响应:', response);
      
      // 根据实际响应结构处理
      let userData = null;
      if (response && response.success && response.data) {
        // 如果有 success 字段，说明包装了一层
        userData = response.data;
      } else if (response && response.id) {
        // 如果直接返回用户数据
        userData = response;
      }
      
      if (userData) {
        console.log('用户数据:', userData);
        console.log('用户头像URL:', userData.avatar);
        if (userData.avatar) {
          setUserAvatar(userData.avatar);
        } else {
          console.log('用户头像字段为空');
          setUserAvatar(null);
        }
      } else {
        console.log('未获取到用户数据');
      }
    } catch (error) {
      console.error('获取用户头像失败:', error);
    }
  };

  // 获取系统名称和企业LOGO
  useEffect(() => {
    const fetchSystemInfo = async () => {
      try {
        let params = [];
        if (localStorage.getItem('token')) {
          const response = await systemParamAPI.getSystemParams({
            page: 0,
            size: 100,
          });
          if (response && response.success) {
            params = response.data || [];
          }
        } else {
          // 未登录（登录页 / 维修门户）走免登录公开配置，避免 401 报错
          const pub = await authAPI.getPublicConfig();
          if (pub && pub.success && pub.data) {
            params = Object.keys(pub.data).map((key) => ({
              paramKey: key,
              paramValue: pub.data[key],
              status: 1,
            }));
          }
        }
        const systemNameParam = params.find(
          param => param.paramKey === 'system_name' && param.status === 1
        );
        if (systemNameParam && systemNameParam.paramValue) {
          setSystemName(systemNameParam.paramValue);
        }
        const logoParam = params.find(
          param => param.paramKey === 'company_logo'
        );
        if (logoParam && logoParam.paramValue) {
          setCompanyLogo(logoParam.paramValue);
        }
      } catch (error) {
        console.error('获取系统信息失败:', error);
      }
    };

    fetchSystemInfo();
  }, []);

  // 路由和标签页标题映射
  const routeTitleMap = {
    '/': '仪表盘',
    '/products': '商品管理',
    '/products/create': '新建商品',
    '/product-categories': '商品分类管理',
    '/suppliers': '供应商管理',
    '/suppliers/create': '新建供应商',
    '/brands': '商品品牌库',
    '/customers': '客户管理',
    '/customers/create': '新建客户',
    '/purchase-orders': '采购管理',
    '/purchase-orders/create': '新建采购订单',
    '/purchase-invoices': '发票管理',
    '/purchase-return-list': '采购退货',
    '/purchase-return-create': '新建退货单',
    '/sales-orders': '销售管理',
    '/sales-orders/create': '新建销售订单',
    '/inventory': '库存管理',
    '/inventory-alert': '库存预警',
    '/warehouses': '仓库管理',
    '/reports': '报表统计',
    '/staff': '用户管理',
    '/staff/create': '新建用户',
    '/roles': '角色管理',
    '/system-params': '系统配置',
    '/user-profile': '个人中心',
    '/cash-flows': '资金流水',
    '/system-logs': '系统日志',
    '/order-recycle-bin': '订单回收站',
    '/repair-orders': '维修工单',
    '/repair-customers': '维修客户',
    '/repair-settlements': '维修结算',
    '/repair-stats': '维修统计',
  };

  // 获取路由标题，支持动态路由
  const getRouteTitle = (pathname) => {
    // 先尝试精确匹配
    if (routeTitleMap[pathname]) {
      return routeTitleMap[pathname];
    }
    
    // 处理动态路由
    if (pathname.match(/^\/repair-orders\/\d+$/)) {
      return '维修工单详情';
    }
    if (pathname.includes('/products/') && pathname.includes('/edit')) {
      return '编辑商品';
    }
    if (pathname.match(/^\/products\/\d+$/)) {
      return '商品详情';
    }
    if (pathname.includes('/suppliers/edit/')) {
      return '编辑供应商';
    }
    if (pathname.match(/^\/suppliers\/\d+$/)) {
      return '供应商详情';
    }
    if (pathname.includes('/customers/') && pathname.includes('/edit')) {
      return '编辑客户';
    }
    if (pathname.match(/^\/customers\/\d+$/)) {
      return '客户详情';
    }
    if (pathname.includes('/purchase-orders/') && pathname.includes('/edit')) {
      return '编辑采购订单';
    }
    if (pathname.match(/^\/purchase-orders\/\d+$/)) {
      return '采购订单详情';
    }
    if (pathname.includes('/purchase-return-detail/')) {
      return '退货单详情';
    }
    if (pathname.includes('/purchase-return-edit/')) {
      return '编辑退货单';
    }
    if (pathname.includes('/sales-orders/') && pathname.includes('/edit')) {
      return '编辑销售订单';
    }
    if (pathname.match(/^\/sales-orders\/\d+$/)) {
      return '销售订单详情';
    }
    if (pathname.includes('/staff/') && pathname.includes('/edit')) {
      return '编辑用户';
    }
    if (pathname.match(/^\/staff\/\d+$/)) {
      return '用户详情';
    }
    
    return '未知页面';
  };

  // 监听路由变化，自动添加或切换标签页
  useEffect(() => {
    const pathname = location.pathname;
    
    // 过滤掉不需要创建标签的路径
    const skipPaths = ['/login'];
    if (skipPaths.includes(pathname)) {
      return;
    }
    
    // 检查是否是未知路径
    const title = getRouteTitle(pathname);
    if (title === '未知页面') {
      console.warn('未知路径:', pathname);
      return;
    }
    
    // 使用函数式更新，原子性地检查并添加标签，避免闭包旧值导致重复添加
    setTabs(prev => {
      if (prev.find(tab => tab.key === pathname)) return prev;
      return [...prev, { key: pathname, label: title, closable: pathname !== '/' }];
    });
    
    setActiveKey(pathname);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // 切换标签页
  const onTabChange = (key) => {
    setActiveKey(key);
    navigate(key);
  };

  // 关闭标签页 —— 从 ref 读取最新 tabs，避免闭包旧值导致关闭失败
  const onTabEdit = (targetKey, action) => {
    if (action !== 'remove') return;
    
    const currentTabs = tabsRef.current;
    const targetIndex = currentTabs.findIndex(tab => tab.key === targetKey);
    if (targetIndex === -1) return;
    
    const newTabs = currentTabs.filter(tab => tab.key !== targetKey);
    setTabs(newTabs);
    
    // 如果关闭的是当前活动标签，切换到相邻标签
    if (targetKey === activeKey) {
      let newActiveKey = '/';
      if (targetIndex > 0 && newTabs[targetIndex - 1]) {
        newActiveKey = newTabs[targetIndex - 1].key;
      } else if (newTabs.length > 0) {
        newActiveKey = newTabs[0].key;
      }
      setActiveKey(newActiveKey);
      navigate(newActiveKey);
    }
  };
  
  // 一键关闭所有标签页（除了仪表盘）
  const handleCloseAllTabs = () => {
    Modal.confirm({
      title: '确认关闭',
      content: '您确定要关闭所有功能标签页吗？',
      onOk: () => {
        // 只保留仪表盘标签
        const dashboardTab = tabsRef.current.find(tab => tab.key === '/');
        setTabs(dashboardTab ? [dashboardTab] : [{
          key: '/',
          label: '仪表盘',
          closable: false,
        }]);
        // 切换到仪表盘
        setActiveKey('/');
        navigate('/');
        message.success('已关闭所有功能标签页');
      },
    });
  };

  // 刷新指定标签页：若目标不是当前页先切换过去，再通过 refreshTick 强制重建路由组件
  const handleRefreshTab = (targetKey) => {
    if (targetKey !== activeKey) {
      setActiveKey(targetKey);
      navigate(targetKey);
    }
    setRefreshTick(t => t + 1);
    message.success('页面已刷新');
  };

  // 关闭其他标签页（保留仪表盘和目标标签，并切换到目标）
  const handleCloseOtherTabs = (targetKey) => {
    const currentTabs = tabsRef.current;
    const newTabs = currentTabs.filter(t => t.key === '/' || t.key === targetKey);
    setTabs(newTabs);
    if (activeKey !== targetKey) {
      setActiveKey(targetKey);
      navigate(targetKey);
    }
    message.success('已关闭其他标签页');
  };

  // 关闭目标标签左侧的所有标签（仪表盘始终保留）
  const handleCloseLeftTabs = (targetKey) => {
    const currentTabs = tabsRef.current;
    const idx = currentTabs.findIndex(t => t.key === targetKey);
    if (idx <= 0) return;
    setTabs(currentTabs.filter((t, i) => t.key === '/' || i >= idx));
    const activeIdx = currentTabs.findIndex(t => t.key === activeKey);
    if (activeKey !== '/' && activeIdx < idx) {
      setActiveKey(targetKey);
      navigate(targetKey);
    }
    message.success('已关闭左侧标签页');
  };

  // 关闭目标标签右侧的所有标签
  const handleCloseRightTabs = (targetKey) => {
    const currentTabs = tabsRef.current;
    const idx = currentTabs.findIndex(t => t.key === targetKey);
    if (idx === -1) return;
    setTabs(currentTabs.filter((t, i) => t.key === '/' || i <= idx));
    const activeIdx = currentTabs.findIndex(t => t.key === activeKey);
    if (activeKey !== '/' && activeIdx > idx) {
      setActiveKey(targetKey);
      navigate(targetKey);
    }
    message.success('已关闭右侧标签页');
  };

  // 根据路由返回标签页图标，增强标签可视化
  const getTabIcon = (pathname) => {
    if (pathname === '/') return <DashboardOutlined />;
    if (pathname.startsWith('/products') || pathname.startsWith('/product-categories') || pathname.startsWith('/brands')) return <ShopOutlined />;
    if (pathname.startsWith('/suppliers')) return <TeamOutlined />;
    if (pathname.startsWith('/customers')) return <UserOutlined />;
    if (pathname.startsWith('/purchase-orders') || pathname.startsWith('/purchase-invoices') || pathname.startsWith('/purchase-return')) return <ShoppingCartOutlined />;
    if (pathname.startsWith('/sales-orders')) return <DollarOutlined />;
    if (pathname.startsWith('/inventory') || pathname.startsWith('/warehouses')) return <DatabaseOutlined />;
    if (pathname.startsWith('/reports')) return <BarChartOutlined />;
    if (pathname.startsWith('/staff') || pathname.startsWith('/roles')) return <UsergroupAddOutlined />;
    if (pathname.startsWith('/cash-flows')) return <DollarOutlined />;
    if (pathname.startsWith('/system-params') || pathname.startsWith('/system-logs')) return <SettingOutlined />;
    if (pathname.startsWith('/user-profile')) return <UserOutlined />;
    if (pathname.startsWith('/order-recycle-bin')) return <DeleteOutlined />;
    if (pathname.startsWith('/repair-')) return <ToolOutlined />;
    return <FileTextOutlined />;
  };

  // 构建单个标签的右键菜单
  const buildTabContextMenu = (tabKey) => ({
    items: [
      { key: 'refresh', icon: <ReloadOutlined />, label: '刷新当前页' },
      { key: 'close', icon: <CloseOutlined />, label: '关闭标签页', disabled: tabKey === '/' },
      { type: 'divider' },
      { key: 'closeOthers', label: '关闭其他标签页' },
      { key: 'closeLeft', label: '关闭左侧标签页' },
      { key: 'closeRight', label: '关闭右侧标签页' },
      { type: 'divider' },
      { key: 'closeAll', label: '关闭全部标签页' },
    ],
    onClick: ({ key }) => {
      switch (key) {
        case 'refresh': handleRefreshTab(tabKey); break;
        case 'close': onTabEdit(tabKey, 'remove'); break;
        case 'closeOthers': handleCloseOtherTabs(tabKey); break;
        case 'closeLeft': handleCloseLeftTabs(tabKey); break;
        case 'closeRight': handleCloseRightTabs(tabKey); break;
        case 'closeAll': handleCloseAllTabs(); break;
        default: break;
      }
    },
  });

  // 渲染用标签项：在原标题上包装右键菜单 + 鼠标中键关闭
  const tabItems = useMemo(() => tabs.map(tab => ({
    key: tab.key,
    closable: tab.closable,
    label: (
      <Dropdown menu={buildTabContextMenu(tab.key)} trigger={['contextMenu']}>
        <span
          className="tab-label-wrap"
          onMouseDown={(e) => { if (e.button === 1) e.preventDefault(); }}
          onAuxClick={(e) => {
            if (e.button === 1 && tab.closable) {
              e.preventDefault();
              onTabEdit(tab.key, 'remove');
            }
          }}
        >
          <span className="tab-icon">{getTabIcon(tab.key)}</span>
          {tab.label}
        </span>
      </Dropdown>
    ),
  })), [tabs, activeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 工具栏"标签操作"下拉（作用于当前活动标签）
  const tabActionsMenu = {
    items: [
      { key: 'refresh', icon: <ReloadOutlined />, label: '刷新当前页' },
      { type: 'divider' },
      { key: 'closeOthers', label: '关闭其他标签页', disabled: tabs.length <= 2 },
      { key: 'closeLeft', label: '关闭左侧标签页' },
      { key: 'closeRight', label: '关闭右侧标签页' },
      { type: 'divider' },
      { key: 'closeAll', icon: <CloseOutlined />, label: '关闭全部标签页', danger: true, disabled: tabs.length <= 1 },
    ],
    onClick: ({ key }) => {
      switch (key) {
        case 'refresh': handleRefreshTab(activeKey); break;
        case 'closeOthers': handleCloseOtherTabs(activeKey); break;
        case 'closeLeft': handleCloseLeftTabs(activeKey); break;
        case 'closeRight': handleCloseRightTabs(activeKey); break;
        case 'closeAll': handleCloseAllTabs(); break;
        default: break;
      }
    },
  };

  // 退出登录
  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出',
      content: '您确定要退出登录吗？',
      onOk: () => {
        // 清除登录信息
        clearLoginInfo();
        message.success('退出成功');
        // 跳转到登录页
        navigate('/login');
      },
    });
  };

  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'profile',
      icon: <ProfileOutlined />,
      label: <Link to="/user-profile">个人中心</Link>,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: handleLogout,
    },
  ];
  
  const menuItems = useMemo(() => [
    {
      key: '/',
      icon: <AppstoreOutlined />,
      label: <Link to="/">仪表盘</Link>,
    },
    {
      key: '/sales-orders',
      icon: <ShoppingCartOutlined />,
      label: <Link to="/sales-orders">销售管理</Link>,
    },
    {
      key: '/purchase-group',
      icon: <ShoppingCartOutlined />,
      label: '采购管理',
      children: [
        {
          key: '/purchase-orders',
          label: <Link to="/purchase-orders">采购订单</Link>,
        },
        {
          key: '/purchase-invoices',
          label: <Link to="/purchase-invoices">发票管理</Link>,
        },        // 采购退货功能已集成到订单详情页，不再单独显示菜单
        // 保留退货单列表路由供查询历史记录
        // {
        //   key: '/purchase-return-list',
        //   label: <Link to="/purchase-return-list">采购退货</Link>,
        // },
      ],
    },
    {
      key: '/repair-group',
      icon: <ToolOutlined />,
      label: '维修管理',
      children: [
        {
          key: '/repair-orders',
          label: <Link to="/repair-orders">维修工单</Link>,
        },
        {
          key: '/repair-customers',
          label: <Link to="/repair-customers">维修客户</Link>,
        },
        {
          key: '/repair-settlements',
          label: <Link to="/repair-settlements">维修结算</Link>,
        },
        {
          key: '/repair-stats',
          label: <Link to="/repair-stats">维修统计</Link>,
        },
      ],
    },
    {
      key: '/cash-flows',
      icon: <DollarOutlined />,
      label: <Link to="/cash-flows">资金流水</Link>,
    },
    {
      key: '/products-group',
      icon: <AppstoreOutlined />,
      label: '商品管理',
      children: [
        {
          key: '/products',
          label: <Link to="/products">商品列表</Link>,
        },
        {
          key: '/product-categories',
          label: <Link to="/product-categories">商品分类</Link>,
        },
      ],
    },
    {
      key: '/suppliers-group',
      icon: <UserOutlined />,
      label: '供应商管理',
      children: [
        {
          key: '/suppliers',
          label: <Link to="/suppliers">供应商列表</Link>,
        },
        {
          key: '/brands',
          label: <Link to="/brands">商品品牌库</Link>,
        },
      ],
    },
    {
      key: '/customers',
      icon: <TeamOutlined />,
      label: <Link to="/customers">客户管理</Link>,
    },
    {
      key: '/inventory-group',
      icon: <DatabaseOutlined />,
      label: '库存管理',
      children: [
        {
          key: '/inventory',
          label: <Link to="/inventory">库存列表</Link>,
        },
        {
          key: '/warehouses',
          label: <Link to="/warehouses">仓库管理</Link>,
        },
        {
          key: '/inventory-alert',
          label: <Link to="/inventory-alert">库存预警</Link>,
        },
      ],
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: <Link to="/reports">报表统计</Link>,
    },
    {
      key: '/system-group',
      icon: <SettingOutlined />,
      label: '系统管理',
      children: [
        {
          key: '/staff',
          label: <Link to="/staff">用户管理</Link>,
        },
        {
          key: '/departments',
          label: <Link to="/departments">部门管理</Link>,
        },
        {
          key: '/positions',
          label: <Link to="/positions">职位管理</Link>,
        },
        {
          key: '/roles',
          label: <Link to="/roles">角色管理</Link>,
        },
        {
          key: '/system-params',
          label: <Link to="/system-params">系统配置</Link>,
        },
        {
          key: '/system-logs',
          label: <Link to="/system-logs">系统日志</Link>,
        },
      ],
    },
  ], []);

  // 根据角色过滤菜单
  useEffect(() => {
    if (userRole !== null && userRole !== undefined) {
      const filtered = filterMenuByRole(menuItems, userRole);
      setFilteredMenuItems(filtered);
    } else {
      // 不设置菜单，等待 userRole 初始化完成
      setFilteredMenuItems([]);
    }
  }, [userRole, menuItems]);

  // 当前主题
  const { theme: appTheme } = useTheme();

  // 客户自助维修门户：独立布局与独立登录态，不走内部员工鉴权与标签页体系
  if (location.pathname.startsWith('/portal')) {
    return (
      <ConfigProvider locale={zhCN} theme={{
        token: {
          colorPrimary: appTheme.primary,
          colorLink: appTheme.primary,
          colorLinkHover: appTheme.primaryHover,
          borderRadius: 6,
          fontFamily: APP_FONT_FAMILY,
        },
      }}>
        <Suspense fallback={<div style={{ padding: 24 }}>加载中...</div>}>
          <Routes>
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/home" element={<PortalRoute><PortalHome /></PortalRoute>} />
            <Route path="/portal/apply" element={<PortalRoute><PortalApply /></PortalRoute>} />
            <Route path="/portal/orders" element={<PortalRoute><PortalOrderList /></PortalRoute>} />
            <Route path="/portal/orders/:id" element={<PortalRoute><PortalOrderDetail /></PortalRoute>} />
            <Route path="/portal/profile" element={<PortalRoute><PortalProfile /></PortalRoute>} />
            <Route path="*" element={<Navigate to="/portal/login" replace />} />
          </Routes>
        </Suspense>
      </ConfigProvider>
    );
  }

  // 如果未登录，只显示登录页
  if (!isAuthenticated()) {
    return (
      <ConfigProvider locale={zhCN} theme={{
        token: {
          colorPrimary: appTheme.primary,
          colorLink: appTheme.primary,
          colorLinkHover: appTheme.primaryHover,
          borderRadius: 6,
          fontFamily: APP_FONT_FAMILY,
        },
      }}>
        <Suspense fallback={<div style={{ padding: 24 }}>加载中...</div>}>
          <Routes>
            <Route path="*" element={<PublicRoute><Login /></PublicRoute>} />
          </Routes>
        </Suspense>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider locale={zhCN} theme={{
        token: {
          colorPrimary: appTheme.primary,
          colorLink: appTheme.primary,
          colorLinkHover: appTheme.primaryHover,
          borderRadius: 6,
          fontFamily: APP_FONT_FAMILY,
        },
      }}>
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        breakpoint="lg"
        collapsedWidth="0"
        style={{ background: `linear-gradient(180deg, ${appTheme.siderGradientFrom} 0%, ${appTheme.siderGradientTo} 100%)` }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <div className="logo" style={{ height: 64, background: 'rgba(255,255,255,0.12)', margin: '16px 12px', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
              <div style={{ display: 'flex', alignItems: 'center', color: 'white' }}>
                <ShopOutlined style={{ fontSize: 24, marginRight: 8 }} />
                <h3 style={{ color: 'white', textAlign: 'center', lineHeight: '24px', margin: 0, fontSize: 18, fontWeight: 600, letterSpacing: 1 }}>{systemName}</h3>
              </div>
            </div>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[location.pathname]}
              defaultOpenKeys={[]}
              items={filteredMenuItems}
              style={{ background: 'transparent', borderRight: 'none' }}
            />
          </div>
          <div style={{ 
            padding: '16px 0', 
            textAlign: 'center', 
            color: 'rgba(255, 255, 255, 0.5)', 
            fontSize: '12px',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            background: 'transparent'
          }}>
            <div>系统版本 v1.0.0</div>
            <div style={{ marginTop: 4, fontSize: '10px' }}>© 2026 进销存管理系统</div>
          </div>
        </div>
      </Sider>
      <Layout>
        <Header className="site-layout-sub-header-background" style={{ padding: '0 20px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {companyLogo && (
              <Link to="/">
                <img src={companyLogo} alt="LOGO" style={{ height: 36, objectFit: 'contain', cursor: 'pointer' }} />
              </Link>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', zIndex: 1000 }}>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" overlayStyle={{ zIndex: 1001 }}>
              <Space style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  icon={!userAvatar && <UserOutlined />} 
                  src={userAvatar}
                  style={{ backgroundColor: !userAvatar ? '#52c41a' : undefined }}
                />
                <div style={{ textAlign: 'left', marginLeft: 8 }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, lineHeight: '20px' }}>{userName}</div>
                  {userRole && <div style={{ fontSize: '12px', color: '#888', lineHeight: '16px' }}>{getRoleName(userRole)}</div>}
                </div>
              </Space>
            </Dropdown>
          </div>
        </Header>
        
        {/* 标签页导航栏 */}
        <div style={{ 
          background: `linear-gradient(to bottom, #ffffff 0%, ${appTheme.primaryBg} 100%)`, 
          padding: '8px 16px', 
          borderBottom: `2px solid ${appTheme.primaryLighter}`,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          zIndex: 999, 
          position: 'relative', 
          display: 'flex', 
          alignItems: 'center',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Tabs
              type="editable-card"
              hideAdd
              activeKey={activeKey}
              onChange={onTabChange}
              onEdit={onTabEdit}
              items={tabItems}
              size="small"
              style={{ 
                marginBottom: 0,
                fontWeight: 500
              }}
              tabBarStyle={{
                margin: 0,
                border: 'none'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12, flexShrink: 0 }}>
            {tabs.length > 1 && (
              <span className="tab-count-badge">{tabs.length} 个标签</span>
            )}
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleRefreshTab(activeKey)}
              className="tab-toolbar-btn"
              title="刷新当前页"
            />
            <Dropdown menu={tabActionsMenu} trigger={['click']} placement="bottomRight">
              <Button
                type="text"
                size="small"
                icon={<DownOutlined />}
                className="tab-toolbar-btn"
                title="标签操作"
              >
                标签操作
              </Button>
            </Dropdown>
          </div>
        </div>
        
        <Content style={{ margin: 0, background: appTheme.primaryBg }}>
          <div className="site-layout-background" style={{ minHeight: '100%' }}>
            <Suspense fallback={<div style={{ padding: 24 }}>加载中...</div>}>
              <Routes key={refreshTick}>
                {/* 公开路由 */}
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                
                {/* 需要权限的路由 */}
                <Route path="" element={<PermissionRoute><Dashboard /></PermissionRoute>} />
                <Route path="/products" element={<PermissionRoute><ProductList /></PermissionRoute>} />
                <Route path="/products/create" element={<PermissionRoute><ProductCreate /></PermissionRoute>} />
                <Route path="/products/:id" element={<PermissionRoute><ProductDetail /></PermissionRoute>} />
                <Route path="/products/:id/edit" element={<PermissionRoute><ProductEdit /></PermissionRoute>} />
                <Route path="/product-categories" element={<PermissionRoute><ProductCategoryList /></PermissionRoute>} />
                <Route path="/suppliers" element={<PermissionRoute><SupplierList /></PermissionRoute>} />
                <Route path="/suppliers/create" element={<PermissionRoute><SupplierCreate /></PermissionRoute>} />
                <Route path="/suppliers/:id" element={<PermissionRoute><SupplierDetail /></PermissionRoute>} />
                <Route path="/suppliers/edit/:id" element={<PermissionRoute><SupplierEdit /></PermissionRoute>} />
                <Route path="/brands" element={<PermissionRoute><BrandList /></PermissionRoute>} />
                <Route path="/customers" element={<PermissionRoute><CustomerList /></PermissionRoute>} />
                <Route path="/customers/create" element={<PermissionRoute><CustomerCreate /></PermissionRoute>} />
                <Route path="/customers/:id" element={<PermissionRoute><CustomerDetail /></PermissionRoute>} />
                <Route path="/customers/:id/edit" element={<PermissionRoute><CustomerEdit /></PermissionRoute>} />
                <Route path="/purchase-orders" element={<PermissionRoute><PurchaseOrderList /></PermissionRoute>} />
                <Route path="/purchase-orders/create" element={<PermissionRoute><PurchaseOrderCreate /></PermissionRoute>} />
                <Route path="/purchase-orders/:id" element={<PermissionRoute><PurchaseOrderDetail /></PermissionRoute>} />
                <Route path="/purchase-orders/:id/edit" element={<PermissionRoute><PurchaseOrderEdit /></PermissionRoute>} />
                <Route path="/purchase-invoices" element={<PermissionRoute><PurchaseInvoiceList /></PermissionRoute>} />
                <Route path="/purchase-return-list" element={<PermissionRoute><PurchaseReturnList /></PermissionRoute>} />
                <Route path="/purchase-return-create" element={<PermissionRoute><PurchaseReturnCreate /></PermissionRoute>} />
                <Route path="/purchase-return-detail/:id" element={<PermissionRoute><PurchaseReturnDetail /></PermissionRoute>} />
                <Route path="/purchase-return-edit/:id" element={<PermissionRoute><PurchaseReturnCreate /></PermissionRoute>} />
                <Route path="/sales-orders" element={<PermissionRoute><SalesOrderList /></PermissionRoute>} />
                <Route path="/sales-orders/create" element={<PermissionRoute><SalesOrderCreate /></PermissionRoute>} />
                <Route path="/sales-orders/:id" element={<PermissionRoute><SalesOrderDetail /></PermissionRoute>} />
                <Route path="/sales-orders/:id/edit" element={<PermissionRoute><SalesOrderEdit /></PermissionRoute>} />
                <Route path="/inventory" element={<PermissionRoute><InventoryList /></PermissionRoute>} />
                <Route path="/warehouses" element={<PermissionRoute><WarehouseList /></PermissionRoute>} />
                <Route path="/inventory-alert" element={<PermissionRoute><InventoryAlert /></PermissionRoute>} />
                <Route path="/reports" element={<PermissionRoute><Reports /></PermissionRoute>} />
                <Route path="/cash-flows" element={<PermissionRoute><CashFlowList /></PermissionRoute>} />
                <Route path="/repair-orders" element={<PermissionRoute><RepairOrderList /></PermissionRoute>} />
                <Route path="/repair-orders/:id" element={<PermissionRoute><RepairOrderDetail /></PermissionRoute>} />
                <Route path="/repair-customers" element={<PermissionRoute><RepairCustomerList /></PermissionRoute>} />
                <Route path="/repair-settlements" element={<PermissionRoute><RepairSettlementList /></PermissionRoute>} />
                <Route path="/repair-stats" element={<PermissionRoute><RepairStats /></PermissionRoute>} />
                <Route path="/staff" element={<PermissionRoute><StaffList /></PermissionRoute>} />
                <Route path="/staff/create" element={<PermissionRoute><StaffCreate /></PermissionRoute>} />
                <Route path="/staff/:id" element={<PermissionRoute><StaffDetail /></PermissionRoute>} />
                <Route path="/staff/:id/edit" element={<PermissionRoute><StaffEdit /></PermissionRoute>} />
                <Route path="/roles" element={<PermissionRoute><RoleList /></PermissionRoute>} />
                <Route path="/departments" element={<PermissionRoute><DepartmentList /></PermissionRoute>} />
                <Route path="/positions" element={<PermissionRoute><PositionList /></PermissionRoute>} />
                <Route path="/system-params" element={<PermissionRoute><SystemParams /></PermissionRoute>} />
                <Route path="/system-logs" element={<PermissionRoute><SystemLogs /></PermissionRoute>} />
                <Route path="/order-recycle-bin" element={<PermissionRoute><OrderRecycleBin /></PermissionRoute>} />
                <Route path="/user-profile" element={<PermissionRoute><UserProfile /></PermissionRoute>} />
              </Routes>
            </Suspense>
          </div>
        </Content>
      </Layout>
    </Layout>
    </ConfigProvider>
  );
};

export default App;